import React, { Fragment } from 'react'
import {
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Tooltip,
  Typography
} from '@mui/material'
import { ErrorMessage, Field, FieldArray, Form, Formik } from 'formik'
import { CheckboxWithLabel, Select, TextField } from 'formik-mui'
import {
  ICreateWarehouse,
  Warehouse
} from '../../../interfaces/Warehouse/Warehouse'
import { warehouseValidationSchema } from '../warehouseConfig'
import AddBoxIcon from '@mui/icons-material/AddBox'
import { DeleteIconButton } from '../../Buttons/DeleteIconButton'
import InfoIcon from '@mui/icons-material/Info'

interface CreateAlmacenProps {
  warehouseEditable?: ICreateWarehouse
  handleSubmit: any
}

const initialValues: Warehouse = {
  id: 0,
  warehouseCode: '',
  postalCode: '',
  warehouseDescription: '',
  zones: [],
  zoneId: 0,
  warehouseAddress: '',
  warehouseName: '',
  warehouseSchedule: [],
  active: false
}

const arrDays = [
  { value: 1, day: 'Lunes' },
  { value: 2, day: 'Martes' },
  { value: 3, day: 'Miercoles' },
  { value: 4, day: 'Jueves' },
  { value: 5, day: 'Viernes' },
  { value: 6, day: 'Sabado' },
  { value: 7, day: 'Domingo' }
]

export const CreateAlmacen: React.FC<CreateAlmacenProps> = ({
  warehouseEditable,
  handleSubmit
}) => {
  return (
    <Fragment>
      <Formik
        initialValues={
          warehouseEditable && warehouseEditable.id
            ? warehouseEditable
            : initialValues
        }
        onSubmit={async (values) => new Promise(() => handleSubmit(values))}
        validationSchema={warehouseValidationSchema}
      >
        {({ values, isSubmitting, isValid, errors }) => (
          <Form autoComplete='off'>
            <Grid container direction='row' spacing={2}>
              <Grid item xs={12} md={4}>
                <Field
                  fullWidth
                  name='warehouseCode'
                  component={TextField}
                  label='Código Almacén'
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Field
                  fullWidth
                  name='postalCode'
                  component={TextField}
                  label='Código Postal'
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Field
                  fullWidth
                  name='warehouseName'
                  component={TextField}
                  label='Nombre Almacén'
                />
              </Grid>

              <Grid item xs={12} sm={10}>
                <Field
                  fullWidth
                  name='warehouseAddress'
                  component={TextField}
                  label='Dirección Almacén'
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Field
                  name='active'
                  component={CheckboxWithLabel}
                  Label={{ label: 'Activo' }}
                  type='checkbox'
                  color='default'
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  fullWidth
                  name='warehouseDescription'
                  component={TextField}
                  label='Descripción Almacén'
                  multiline
                  rows={3}
                  variant='outlined'
                />
              </Grid>
              {warehouseEditable && warehouseEditable.id && (
                <React.Fragment>
                  <Grid item xs={12} sm={6} md={6}>
                    <InputLabel id='zoneId'>Zona predeterminada</InputLabel>
                    <Field fullWidth name='zoneId' component={Select}>
                      {warehouseEditable.zones.map((zone) => (
                        <MenuItem selected={zone.default} value={zone.zoneId}>
                          {zone.zoneName}
                        </MenuItem>
                      ))}
                    </Field>
                  </Grid>

                  <Grid item xs={12} sm={6} md={6}>
                    <Tooltip
                      title={`Si el SKU no tiene stock en la bodega
                  al momento de crear una ruta de picking, 
                  será asignado a esta zona`}
                      aria-label='info'
                    >
                      <div>
                        <IconButton id={`info`} name={`info`} aria-label='info'>
                          <InfoIcon />
                        </IconButton>
                      </div>
                    </Tooltip>
                  </Grid>
                </React.Fragment>
              )}
            </Grid>

            <FieldArray name='warehouseSchedule'>
              {({ push, remove }) => (
                <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                  <Grid
                    container
                    direction='row'
                    alignItems='center'
                    justifyContent='center'
                  >
                    <Grid item xs={6}>
                      <Typography variant='h6'>Horarios</Typography>
                    </Grid>
                    <Grid item xs={6} style={{ textAlign: 'right' }}>
                      <IconButton
                        aria-label='añadir horario'
                        disabled={
                          isSubmitting || values.warehouseSchedule.length >= 7
                        }
                        onClick={() =>
                          push({
                            id: values.id,
                            dayId: values.warehouseSchedule.length + 1,
                            start: '',
                            end: ''
                          })
                        }
                        color='primary'
                      >
                        <AddBoxIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                  {values.warehouseSchedule.map((_, index) => (
                    <div style={{ marginTop: '30px', marginBottom: '30px' }}>
                      <Grid
                        container
                        item
                        key={index}
                        spacing={1}
                        direction='row'
                        justifyContent='center'
                      >
                        <Grid item xs={12} sm={3} md={3}>
                          <InputLabel id='dia'>Dia</InputLabel>
                          <Field
                            fullWidth
                            name={`warehouseSchedule[${index}].dayId`}
                            component={Select}
                          >
                            {arrDays.map((elemento) => {
                              return (
                                <MenuItem value={elemento.value}>
                                  {elemento.day}
                                </MenuItem>
                              )
                            })}
                          </Field>
                          <p
                            style={{
                              color: '#f44336',
                              fontSize: '13px'
                            }}
                          >
                            <ErrorMessage name='rolId' />
                          </p>
                        </Grid>

                        <Grid item xs={12} sm={3} md={4}>
                          <Field
                            fullWidth
                            name={`warehouseSchedule[${index}].start`}
                            component={TextField}
                            label='Hora Inicio'
                            type='time'
                            defaultValue='07:30'
                            labelId='tipo-perfil-rol'
                            InputLabelProps={{
                              shrink: true
                            }}
                            inputProps={{
                              step: 300 // 5 min
                            }}
                            ampm='false'
                          />
                        </Grid>

                        <Grid item xs={12} sm={3} md={4}>
                          <Field
                            fullWidth
                            name={`warehouseSchedule[${index}].end`}
                            component={TextField}
                            label='Hora Fin'
                            type='time'
                            defaultValue='07:30'
                            labelId='tipo-perfil-rol'
                            InputLabelProps={{
                              shrink: true
                            }}
                            inputProps={{
                              step: 300 // 5 min
                            }}
                            ampm='false'
                          />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sm={3}
                          md={1}
                          style={{ textAlign: 'center' }}
                        >
                          <DeleteIconButton
                            isDisabled={isSubmitting}
                            onClick={() => remove(index)}
                          />
                        </Grid>
                      </Grid>
                    </div>
                  ))}

                  <Grid item>
                    {typeof errors.warehouseSchedule! === 'string' ? (
                      <Typography color='error'>
                        {errors.warehouseSchedule}
                      </Typography>
                    ) : null}
                  </Grid>
                </div>
              )}
            </FieldArray>

            <Grid container spacing={0}>
              <Grid item xs={12}>
                <Button
                  disabled={isSubmitting || !isValid}
                  fullWidth
                  type='submit'
                  variant='contained'
                  color={isValid ? 'primary' : 'secondary'}
                  style={{
                    marginTop: '10px'
                  }}
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size='0.9rem' />
                    ) : undefined
                  }
                >
                  {isSubmitting ? '' : 'Guardar'}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Fragment>
  )
}
